import { useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"

import { useEvent } from "shared/lib/useEvent"

import { CopyProjectChildrenOverlayModel } from "../CopyProjectChildrenOverlay"
import {
  CopyProjectChildrenFormData,
  CopyProjectChildrenOverlayViewModel
} from "../CopyProjectChildrenOverlayView"

export function useCopyProjectChildrenOverlayViewModel({
  defaultFormData,
  onFormDataChange,
  onSubmit,
  onCancel,
  isSubmitting,
  ...rest
}: CopyProjectChildrenOverlayModel): CopyProjectChildrenOverlayViewModel {
  const methods = useForm({ defaultValues: defaultFormData })
  const formDataRef = useRef(defaultFormData)
  const [formData, setFormData] = useState(defaultFormData)

  const handleSubmit = useMemo(
    () => methods.handleSubmit(onSubmit),
    [onSubmit, methods.handleSubmit]
  )

  const updateFormData = useEvent(
    (nextFormData: CopyProjectChildrenFormData) => {
      formDataRef.current = nextFormData
      setFormData(nextFormData)
      onFormDataChange(nextFormData)
    }
  )

  useEffect(() => {
    const subscription = methods.watch(nextFormData => {
      if (
        nextFormData.projectID &&
        nextFormData.projectID !== formDataRef.current.projectID
      ) {
        const resetFormData: CopyProjectChildrenFormData = {
          projectID: nextFormData.projectID,
          parentID: nextFormData.projectID,
          position: 0
        }

        updateFormData(resetFormData as CopyProjectChildrenFormData)
        methods.reset(resetFormData)
      } else {
        updateFormData(nextFormData as CopyProjectChildrenFormData)
      }
    })
    return () => subscription.unsubscribe()
  }, [methods.watch, methods.reset, onFormDataChange])

  const hideParentInput = rest.parentOptions.length <= 1
  const hidePositionInput = !formData.projectID

  return {
    ...rest,
    methods,
    hideParentInput,
    hidePositionInput,
    isSubmitting,
    onSubmit: handleSubmit,
    onCancel
  }
}
